import clsx from "clsx";
import { FC } from "react";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";
import { i18n, Loader, useIsTouchDevice } from "rujira.ui";
import { usePreloadedAccountData } from "../../services/accountData";
import {
  BalancesPoolFragment$data,
  BalancesPoolFragment$key,
} from "./__generated__/BalancesPoolFragment.graphql";
import { BowPoolXykRow, BowPoolXykThumb } from "./BowPoolXykThumb";
import { GhostVaultRow, GhostVaultThumb } from "./GhostVaultThumb";
import { IndexVaultPortfolioRow } from "./IndexVaultPortfolioRow";
import { IndexVaultThumb } from "./IndexVaultThumb";
import { PerpsPoolRow, PerpsPoolThumb } from "./PerpsPoolThumb";
import { StakingPoolPortfolioRow } from "./StakingPoolPortfolioRow";
import { StakingPoolThumb } from "./StakingPoolThumb";
import { ThorchainPoolRow, ThorchainPoolThumb } from "./ThorchainPoolThumb";

const fragment = graphql`
  fragment BalancesPoolFragment on Account {
    bow {
      id
      ...BalanceThumbDualFragment
    }
    strategies {
      __typename
      ... on ThorchainLiquidityProvider {
        id
        ...ThorchainPoolThumbFragment
      }
      ... on BowAccount {
        id
        ...BowPoolXykThumbFragment
      }
      ... on PerpsAccount {
        id
        ...PerpsPoolThumbFragment
      }
      ... on IndexAccount {
        id
        ...IndexVaultThumbFragment
        ...IndexVaultPortfolioRowFragment
      }
      ... on StakingAccount {
        id
        ...StakingPoolThumbFragment
        ...StakingPoolPortfolioRowFragment
      }
      ... on GhostVaultAccount {
        id
        ...GhostVaultThumbFragment
      }
    }
  }
`;

export const Balances: FC<{ a?: BalancesPoolFragment$key }> = () => {
  const { accountData } = usePreloadedAccountData();

  const account = useFragment<BalancesPoolFragment$key>(fragment, accountData);

  return (
    <div className="pools__positions">
      <i18n.h3 className="h3 mt-5 mt-lg-12">My Positions</i18n.h3>
      <div className="pools__grid">
        {account?.strategies.map((x) => {
          return "id" in x ? <Thumb key={x.id} item={x} /> : null;
        })}
        {(!account?.strategies || account?.strategies.length === 0) && (
          <div className="relative card p-3">
            <i18n.h4 className="h4 fs-16 lh-19 fw-500 color-grey text-center mb-0">
              No positions found
            </i18n.h4>
          </div>
        )}
      </div>
    </div>
  );
};

export const BalancesFallback = () => (
  <div className="pools__positions">
    <i18n.h3 className="h3 mt-5 mt-lg-12 flex">
      My Positions <Loader className=" ml-2 mt-1 w-4 h-4" />
    </i18n.h3>
  </div>
);

const Thumb: FC<{
  item: NonNullable<BalancesPoolFragment$data>["strategies"][number];
}> = ({ item }) => {
  switch (item.__typename) {
    case "ThorchainLiquidityProvider":
      return <ThorchainPoolThumb k={item} />;
    case "BowAccount":
      return <BowPoolXykThumb k={item} />;
    case "PerpsAccount":
      return <PerpsPoolThumb k={item} />;
    case "IndexAccount":
      return <IndexVaultThumb k={item} />;
    case "StakingAccount":
      return <StakingPoolThumb k={item} />;
    case "GhostVaultAccount":
      return <GhostVaultThumb k={item} />;
  }
};

export const StrategiesPortfolio: FC<{ a?: BalancesPoolFragment$key }> = () => {
  const { accountData } = usePreloadedAccountData();
  const account = useFragment<BalancesPoolFragment$key>(fragment, accountData);
  const isTouchDevice = useIsTouchDevice();

  return (
    <div className="table-sticky table-sticky--left">
      {account?.strategies.length ? (
        <table
          className={clsx({
            "table table--big condensed portfolio__balances": true,
            "table--no-hover": isTouchDevice,
          })}>
          <thead>
            <tr>
              <i18n.th className="w-1">Strategy</i18n.th>
              <th></th>
              <i18n.th>Type</i18n.th>
              <i18n.th>Assets</i18n.th>
              <i18n.th className="text-right">APR</i18n.th>
              <i18n.th className="text-right">Value</i18n.th>
            </tr>
          </thead>
          <tbody>
            {account?.strategies.map((x) => {
              return "id" in x ? <PortfolioRow key={x.id} item={x} /> : null;
            })}
          </tbody>
        </table>
      ) : (
        <i18n.div className="fs-16 lh-19 fw-500 color-grey text-center">
          No strategy balances found
        </i18n.div>
      )}
    </div>
  );
};

const PortfolioRow: FC<{
  item: NonNullable<BalancesPoolFragment$data>["strategies"][number];
}> = ({ item }) => {
  switch (item.__typename) {
    case "ThorchainLiquidityProvider":
      return <ThorchainPoolRow k={item} />;
    case "BowAccount":
      return <BowPoolXykRow k={item} />;
    case "PerpsAccount":
      return <PerpsPoolRow k={item} />;
    case "IndexAccount":
      return <IndexVaultPortfolioRow k={item} />;
    case "StakingAccount":
      return <StakingPoolPortfolioRow k={item} />;
    case "GhostVaultAccount":
      return <GhostVaultRow k={item} />;
  }
};
